import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import { InviteUserRequest, UserRole, userService } from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ALL_USER_ROLES } from '../utils/userRoles'

const ISRAELI_PHONE = /^(\+972|0)([234789]|5[0123458]|77)[0-9]{7}$/

export interface InviteUserDialogProps {
  open: boolean
  onClose: () => void
  /** The account being viewed (for context only — see disclaimer below). */
  targetAccountId?: string
  defaultRoles?: UserRole[]
  onInvited?: () => void
}

export default function InviteUserDialog({
  open,
  onClose,
  targetAccountId,
  defaultRoles,
  onInvited,
}: InviteUserDialogProps) {
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [roles, setRoles] = useState<UserRole[]>(defaultRoles ?? [])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setName('')
      setEmail('')
      setPhone('')
      setRoles(defaultRoles ?? [])
      setError(null)
    }
  }, [open, defaultRoles])

  const inviteMutation = useMutation({
    mutationFn: (request: InviteUserRequest) => userService.invite(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onInvited?.()
      onClose()
    },
    onError: (e: any) =>
      setError(e?.response?.data?.message || 'Failed to invite user'),
  })

  const phoneValid = !phone || ISRAELI_PHONE.test(phone)
  const canSubmit = name.trim() && phone.trim() && phoneValid && roles.length > 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning" variant="outlined">
            Heads up: the backend invite endpoint puts the new user into the{' '}
            <strong>inviter's own account</strong>, not the selected account
            {targetAccountId ? ` (${targetAccountId.substring(0, 8)}…)` : ''}.
            Use this only when inviting into your own admin account, or ask a
            foreman / distribution manager of the target account to invite from
            their own console.
          </Alert>
          <TextField
            label="Full name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Phone *"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            size="small"
            placeholder="+972501234567 or 0501234567"
            error={!!phone && !phoneValid}
            helperText={
              phone && !phoneValid
                ? 'Israeli format required'
                : 'Israeli format: +972… or 0…'
            }
          />
          <FormControl fullWidth size="small">
            <InputLabel>Roles *</InputLabel>
            <Select
              multiple
              value={roles}
              onChange={(e) =>
                setRoles(
                  (typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : e.target.value) as UserRole[]
                )
              }
              input={<OutlinedInput label="Roles *" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip
                      key={value}
                      label={getLocalizedLabel('userRole', value) || value}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {ALL_USER_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {getLocalizedLabel('userRole', role) || role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || inviteMutation.isPending}
          onClick={() =>
            inviteMutation.mutate({
              name: name.trim(),
              email: email.trim() || undefined,
              phone: phone.trim(),
              roles,
            })
          }
        >
          {inviteMutation.isPending ? 'Inviting…' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
